import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "flex gap-2" }
const _hoisted_2 = { class: "flex flex-1 flex-col gap-2" }
const _hoisted_3 = { class: "flex flex-1 flex-col gap-2" }

import { computed } from 'vue';
    import { useDateFormat } from '@/composables/useDateFormat';
    import DatePicker from '@/components/ui/form/DatePicker.vue';
    import { CalendarDate } from '@internationalized/date';
    import { useI18n } from 'vue-i18n';
    import { Label } from '@/components/ui/label';
    import type { Column } from '@tanstack/vue-table';

    interface Props {
        column: Column<any, unknown>;
        modelValue?: any;
    }

    interface Emits {
        (e: 'update:modelValue', value: string | undefined): void;
    }

    
export default /*@__PURE__*/_defineComponent({
  __name: 'ColumnFilterDate',
  props: {
    column: {},
    modelValue: {}
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit: __emit }) {

    const props = __props;
    const emit = __emit;
    const { t } = useI18n();

    const { parseDateString } = useDateFormat();

    const normalizeDateFormat = (dateStr: string): string => {
        return dateStr.replace(/[./]/g, '-');
    };

    const detectDateFormat = (dateStr: string): 'dot' | 'slash' | 'dash' => {
        if (dateStr.includes('.')) return 'dot';
        if (dateStr.includes('/')) return 'slash';
        return 'dash';
    };

    const formatToOriginalFormat = (
        day: number,
        month: number,
        year: number,
        format: 'dot' | 'slash' | 'dash',
    ): string => {
        const dayStr = day.toString().padStart(2, '0');
        const monthStr = month.toString().padStart(2, '0');

        switch (format) {
            case 'dot':
                return `${dayStr}.${monthStr}.${year}`;
            case 'slash':
                return `${dayStr}/${monthStr}/${year}`;
            case 'dash':
                return `${dayStr}-${monthStr}-${year}`;
            default:
                return `${dayStr}.${monthStr}.${year}`;
        }
    };

    const fromDate = computed<string | undefined>({
        get() {
            if (!props.modelValue || typeof props.modelValue !== 'string') {
                return undefined;
            }

            const parts = props.modelValue.includes(',')
                ? props.modelValue.split(',')
                : [props.modelValue];

            if (parts.length >= 1 && parts[0].trim()) {
                const startDateStr = normalizeDateFormat(parts[0].trim());
                const calendarDate = parseDateString(startDateStr);

                if (calendarDate) {
                    return calendarDate.toString();
                }
            }
            return undefined;
        },
        set(value: string | undefined) {
            updateFilterValue(value, toDate.value);
        },
    });

    const toDate = computed<string | undefined>({
        get() {
            if (!props.modelValue || typeof props.modelValue !== 'string') {
                return undefined;
            }

            const parts = props.modelValue.includes(',') ? props.modelValue.split(',') : [];

            if (parts.length === 2 && parts[1].trim()) {
                const endDateStr = normalizeDateFormat(parts[1].trim());
                const calendarDate = parseDateString(endDateStr);

                if (calendarDate) {
                    return calendarDate.toString();
                }
            }
            return undefined;
        },
        set(value: string | undefined) {
            updateFilterValue(fromDate.value, value);
        },
    });

    const updateFilterValue = (from: string | undefined, to: string | undefined) => {
        // If both dates are empty, clear the filter
        if (!from && !to) {
            emit('update:modelValue', undefined);
            return;
        }

        let originalFormat: 'dot' | 'slash' | 'dash' = 'dot';
        if (props.modelValue && typeof props.modelValue === 'string') {
            const firstDateStr = props.modelValue.includes(',')
                ? props.modelValue.split(',')[0].trim()
                : props.modelValue;
            if (firstDateStr) {
                originalFormat = detectDateFormat(firstDateStr);
            }
        }

        const formatToTableFormat = (dateStr: string): string => {
            const calendarDate = parseDateString(dateStr);
            if (calendarDate) {
                return formatToOriginalFormat(
                    calendarDate.day,
                    calendarDate.month,
                    calendarDate.year,
                    originalFormat,
                );
            }
            return dateStr;
        };

        // Handle different combinations of from/to dates
        if (from && to) {
            // Both from and to dates
            const fromFormatted = formatToTableFormat(from);
            const toFormatted = formatToTableFormat(to);
            emit('update:modelValue', `${fromFormatted},${toFormatted}`);
        } else if (from) {
            // Only from date
            const fromFormatted = formatToTableFormat(from);
            emit('update:modelValue', fromFormatted);
        } else if (to) {
            // Only to date
            const toFormatted = formatToTableFormat(to);
            emit('update:modelValue', `,${toFormatted}`);
        }
    };

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(_unref(Label), null, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref(t)('forms.from')), 1)
        ]),
        _: 1
      }),
      _createVNode(DatePicker, {
        modelValue: fromDate.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((fromDate).value = $event))
      }, null, 8, ["modelValue"])
    ]),
    _createElementVNode("div", _hoisted_3, [
      _createVNode(_unref(Label), null, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref(t)('forms.to')), 1)
        ]),
        _: 1
      }),
      _createVNode(DatePicker, {
        modelValue: toDate.value,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((toDate).value = $event))
      }, null, 8, ["modelValue"])
    ])
  ]))
}
}

})